import { useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useQueryClient } from '@tanstack/react-query';
import {
  accountSlugProblem,
  AccountSlugProblem,
  accountSlugRefusal,
  AccountSlugRefusal,
  fitAccountSlug,
} from 'fym-shared/src/music24/accountSlug';
import type { OnboardingStateAnswer } from '../../../../../music24-api/src/trpc/onboarding.router';
import { useTRPCAPI, useTRPCClientAPI } from '../../../trpc/trpc';
import { fymAPI } from '../../../redux/api/fymAPI';
import { SMLAnalytics } from '../../analytics/sml/analytics';
import { slugifyArtistName } from '../../accountSelector/slugifyArtistName';
import { AddressState, useAccountSlugCheck } from '../../smartLinks/profile/useAccountSlugCheck';
import { cleanAddressInput, mapWelcomeSaveError, WelcomeSaveError } from './welcomeSaveError';
import { suggestedArtistName } from './suggestedArtistName';

export interface WelcomeForm {
  name: string;
  setName: (value: string) => void;
  slug: string;
  setSlug: (value: string) => void;
  /** True after the user typed in the address field. A name change then no longer moves the address. */
  addressEdited: boolean;
  slugState: AddressState;
  /**
   * What the address rules (`accountSlugProblem`: 2 to 60 characters, no dash at the ends, not an
   * address of Music24) say before any server call, or null (QA 2026-10-28, brk-3 and brk-14).
   */
  slugProblem: AccountSlugProblem | null;
  /**
   * The exact reason why the address cannot be used (too short, too long, characters, reserved):
   * from the save answer first, then the address rules, then the live check. Null when none
   * (QA 2026-10-28, brk-r2-4).
   */
  slugRefusal: AccountSlugRefusal | null;
  /** The free address to offer: from the save error first, else from the live check. */
  suggestion?: string;
  canSave: boolean;
  /** True while the name field is empty: the buttons stay off and the page says why. */
  needsName: boolean;
  isSaving: boolean;
  /** Saves the name and the address. True when the save worked (or when there is no artist to save). */
  save: () => Promise<boolean>;
  error: WelcomeSaveError | null;
}

/**
 * False while the artist name is still the stand-in that signup made from the email local part
 * (onboarding review 2026-10-19, finding 3). An API from before the field sends nothing: then the
 * saved name counts as chosen.
 */
export function isArtistNameChosen(artist: OnboardingStateAnswer['artist']): boolean {
  if (artist === null) {
    return true;
  }
  return artist.nameChosen ?? true;
}

/**
 * The name and the address of the welcome page (design: onboarding a1, a6).
 * A stand-in name (`nameChosen: false`) starts the field with a cleaned copy (`suggestedArtistName`),
 * or empty when the copy is too short.
 * The address follows `slugifyArtistName(name)` until the user types in the address field.
 * An address the user chose before (`slugChosen`) never follows the name.
 */
export function useWelcomeForm(state: OnboardingStateAnswer | undefined): WelcomeForm {
  const trpcAPI = useTRPCAPI();
  const trpcClient = useTRPCClientAPI();
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const artist = state?.artist ?? null;
  const savedSlug = artist?.slug ?? '';

  const [name, setNameValue] = useState(() =>
    isArtistNameChosen(artist) ? (artist?.name ?? '') : suggestedArtistName(artist?.name ?? '')
  );
  const [addressEdited, setAddressEdited] = useState(artist?.slugChosen === true);
  const [editedSlug, setEditedSlug] = useState(savedSlug);
  const [error, setError] = useState<WelcomeSaveError | null>(null);
  const [serverSuggestion, setServerSuggestion] = useState<string>();
  const [serverRefusal, setServerRefusal] = useState<AccountSlugRefusal | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // The address made from the name is cut at 60 characters: a long name gave an 80-character
  // address that the page marked as available and the save refused (brk-3).
  const slug = addressEdited ? editedSlug : fitAccountSlug(slugifyArtistName(name));
  // The saved address stays usable as it is: an old account can hold one that is reserved now.
  const slugProblem = slug === '' || slug === savedSlug ? null : accountSlugProblem(slug);
  // A refused address asks the server nothing: the page says why at once.
  const check = useAccountSlugCheck(slugProblem === null ? slug : '', artist?.accountId, savedSlug);

  const clearError = useCallback(() => {
    setError(null);
    setServerSuggestion(undefined);
    setServerRefusal(null);
  }, []);

  const setName = useCallback(
    (value: string) => {
      setNameValue(value);
      clearError();
    },
    [clearError]
  );

  const setSlug = useCallback(
    (value: string) => {
      setAddressEdited(true);
      setEditedSlug(cleanAddressInput(value));
      clearError();
    },
    [clearError]
  );

  // design: onboarding a6. The buttons are off while the check runs or refuses the address.
  // `idle` is the saved address, or a check that could not run: the save then checks again.
  const slugIsUsable =
    slugProblem === null &&
    (check.state === AddressState.free || check.state === AddressState.idle || slug === savedSlug);
  const blockedBySaveError = error === 'taken' || error === 'reserved' || error === 'invalid';
  const needsName = artist !== null && name.trim() === '';
  const canSave = artist === null || (!needsName && slug !== '' && slugIsUsable && !blockedBySaveError);

  const save = useCallback(async (): Promise<boolean> => {
    if (artist === null) {
      return true;
    }
    setIsSaving(true);
    clearError();
    try {
      await trpcClient.onboarding.saveIdentity.mutate({ name: name.trim(), slug });
      queryClient.invalidateQueries({ queryKey: trpcAPI.onboarding.state.queryKey() }).catch(console.error);
      // The sidebar and the artist switcher read the account over REST: refresh the new name there too.
      dispatch(fymAPI.util.invalidateTags(['AccountForList', { type: 'Account', id: artist.accountId }]));
      SMLAnalytics.track('onboarding_name_saved', {}).catch(console.error);
      return true;
    } catch (reason) {
      const mapped = mapWelcomeSaveError(reason);
      setError(mapped.error);
      setServerSuggestion(mapped.suggestion);
      setServerRefusal(mapped.refusal ?? null);
      return false;
    } finally {
      setIsSaving(false);
    }
  }, [artist, clearError, dispatch, name, queryClient, slug, trpcAPI, trpcClient]);

  const slugRefusal = serverRefusal ?? (slugProblem === null ? check.refusal : accountSlugRefusal(slug)) ?? null;
  const suggestion = serverSuggestion ?? (check.state === AddressState.taken ? check.suggestion : undefined);

  return useMemo(
    () => ({
      name,
      setName,
      slug,
      setSlug,
      addressEdited,
      slugState: check.state,
      slugProblem,
      slugRefusal,
      suggestion,
      canSave: canSave && !isSaving,
      needsName,
      isSaving,
      save,
      error,
    }),
    [
      addressEdited,
      canSave,
      check.state,
      error,
      isSaving,
      name,
      needsName,
      save,
      setName,
      setSlug,
      slug,
      slugProblem,
      slugRefusal,
      suggestion,
    ]
  );
}
